import React, { useRef } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import chevronLeft from "../../assets/Images/TempleDetail/chevron-left.svg";
import chevronRightWhite from "../../assets/Images/TempleDetail/chevron-right-white.svg";
import ctaArrow from "../../assets/Images/TempleDetail/cta-arrow.svg";
import useLoopSlides from "./useLoopSlides";
import { T, FONT, SectionCard, SectionHeading, FitPhoto } from "./shared";


// Arrow drawn inline so it can be white on the red button or grey on the white one.
const Chevron = ({ dir, color, size }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden="true" style={{ display: "block" }}>
    <path d={dir < 0 ? "M11.25 13.5L6.75 9L11.25 4.5" : "M6.75 13.5L11.25 9L6.75 4.5"} stroke={color} strokeWidth="1.65" strokeLinecap="round" />
  </svg>
);

const SLIDE_GAP = 16;

// Figma 3534:13104 "Story — Slides (interactive)": one chapter in focus, the next peeking in.
const StorySlides = ({ story, onDonate }) => {
  const isMobile = useMediaQuery("(max-width:760px)");
  const slides = story?.slides || [];
  // Seamless loop: on the last chapter the first one peeks in on the right and slides in next
  const { pos, index, animate, move, settle, copies } = useLoopSlides(slides.length);
  const touchX = useRef(null);
  if (!slides.length) return null;

  const slideWidth = isMobile ? "calc(100% - 24px)" : "680px";

  const navButton = (dir) => {
    const disabled = slides.length < 2;
    // The story loops, so "next" is always the way forward (red)
    const filled = dir > 0;
    return (
      <Box
        component="button"
        type="button"
        aria-label={dir < 0 ? "Previous chapter" : "Next chapter"}
        disabled={disabled}
        onClick={() => move(dir)}
        sx={{
          width: "42px",
          height: "42px",
          borderRadius: "21px",
          border: filled ? "none" : `1px solid ${T.sand}`,
          background: filled ? T.red : "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.4 : 1,
          flexShrink: 0,
          transition: "opacity 0.2s, transform 0.15s",
          "&:hover": disabled ? {} : { transform: "translateY(-1px)" },
          "&:focus-visible": { outline: `2px solid ${T.ink}`, outlineOffset: "2px" },
        }}
      >
        <Chevron dir={dir} color={filled ? "#FFFFFF" : "#6B6B6B"} size={18} />
      </Box>
    );
  };

  return (
    <SectionCard id="story" sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Box sx={{ display: "flex", alignItems: isMobile ? "flex-start" : "flex-end", justifyContent: "space-between", gap: "16px", flexDirection: isMobile ? "column" : "row" }}>
        <SectionHeading eyebrow="The story" hindi="॥ कथा ॥" title={story.title} />
        <Box sx={{ display: "flex", gap: "12px", alignItems: "center", alignSelf: isMobile ? "flex-end" : "auto" }}>
          <Typography sx={{ fontFamily: FONT, whiteSpace: "nowrap" }} aria-live="polite">
            <Box component="span" sx={{ fontWeight: 700, fontSize: "20px", lineHeight: "24px", color: T.red }}>{String(index + 1).padStart(2, "0")}</Box>
            <Box component="span" sx={{ fontWeight: 500, fontSize: "14px", lineHeight: "20px", color: T.muted }}> / {String(slides.length).padStart(2, "0")}</Box>
          </Typography>
          {navButton(-1)}
          {navButton(1)}
        </Box>
      </Box>

      {/* Slide track */}
      <Box
        sx={{ position: "relative", overflow: "hidden", width: "100%" }}
        onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (Math.abs(dx) > 40) move(dx < 0 ? 1 : -1);
          touchX.current = null;
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: `${SLIDE_GAP}px`,
            transform: `translateX(calc(-${pos} * (${slideWidth} + ${SLIDE_GAP}px)))`,
            transition: animate ? "transform 0.5s cubic-bezier(0.22, 0.61, 0.36, 1)" : "none",
          }}
          onTransitionEnd={(e) => e.target === e.currentTarget && e.propertyName === "transform" && settle()}
        >
          {Array.from({ length: copies }, (_, c) => slides.map((slide, i) => {
            const k = c * slides.length + i;
            const isActive = k === pos;
            return (
              <Box
                key={`${c}-${i}`}
                onClick={() => !isActive && move(k - pos)}
                aria-hidden={!isActive}
                sx={{
                  flex: `0 0 ${slideWidth}`,
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  minHeight: isMobile ? "auto" : "390px",
                  border: "1px solid rgba(201,149,43,0.3)",
                  borderRadius: "22px",
                  overflow: "hidden",
                  background: "#FFFFFF",
                  opacity: isActive ? 1 : 0.5,
                  cursor: isActive ? "default" : "pointer",
                  transition: "opacity 0.4s",
                }}
              >
                <Box sx={{ position: "relative", width: isMobile ? "100%" : "300px", height: isMobile ? "220px" : "auto", flexShrink: 0, background: T.maroon }}>
                  <FitPhoto src={slide.image} />
                  <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(36,5,8,0) 60%, rgba(36,5,8,0.7) 100%)" }} />
                  <Typography sx={{ position: "absolute", left: "18px", bottom: "18px", fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "44px" : "56px", lineHeight: 1, color: "#FFFFFF", opacity: 0.9 }}>
                    {String(i + 1).padStart(2, "0")}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, p: isMobile ? "20px" : "28px", display: "flex", flexDirection: "column", justifyContent: "center", gap: "14px" }}>
                  {slide.tag && (
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "11px", lineHeight: "14px", letterSpacing: "1.1px", textTransform: "uppercase", color: T.red }}>{slide.tag}</Typography>
                  )}
                  {slide.title && (
                    <Typography component="h3" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "22px" : "26px", lineHeight: "32px", color: T.ink }}>{slide.title}</Typography>
                  )}
                  {slide.html ? (
                    // Campaign story (admin rich text), rendered the same way as the regular campaign page
                    <Box
                      sx={{
                        fontFamily: FONT,
                        fontSize: "15px",
                        lineHeight: "24px",
                        color: T.bodyWarm,
                        wordBreak: "break-word",
                        // Admin rich text brings its own inline colours, fonts and sizes; the design wins.
                        "& *": {
                          fontFamily: `${FONT}, sans-serif !important`,
                          fontSize: "inherit !important",
                          lineHeight: "inherit !important",
                          color: "inherit !important",
                          backgroundColor: "transparent !important",
                        },
                        "& p": { m: 0 },
                        "& p + p": { mt: "10px" },
                        // empty editor lines (<p><br></p>) add no gaps
                        "& p:empty, & p:has(> br:only-child)": { display: "none" },
                        "& strong, & b": { color: `${T.ink} !important`, fontWeight: 700 },
                        "& ul, & ol": { pl: "20px", my: "6px" },
                        "& a": { color: `${T.red} !important` },
                      }}
                      dangerouslySetInnerHTML={{ __html: slide.html }}
                    />
                  ) : (
                    <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: "15px", lineHeight: "24px", color: T.bodyWarm }}>{slide.body}</Typography>
                  )}
                  {slide.cta && (
                    <Box
                      component="button"
                      type="button"
                      tabIndex={isActive ? 0 : -1}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDonate?.();
                      }}
                      sx={{
                        mt: "4px",
                        height: "50px",
                        width: "100%",
                        maxWidth: "322px",
                        borderRadius: "14px",
                        border: "none",
                        background: T.red,
                        color: "#FFFFFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        cursor: "pointer",
                        fontFamily: FONT,
                        fontWeight: 700,
                        fontSize: "14px",
                        lineHeight: "20px",
                        "&:hover": { background: "#D41620" },
                        "&:focus-visible": { outline: `2px solid ${T.ink}`, outlineOffset: "2px" },
                      }}
                    >
                      {slide.cta}
                      <Box component="img" src={ctaArrow} alt="" sx={{ width: "16px", height: "16px" }} />
                    </Box>
                  )}
                </Box>
              </Box>
            );
          }))}
        </Box>
      </Box>
    </SectionCard>
  );
};

export default StorySlides;
